import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as pollService from '@/services/poll.service';
import type {
  Poll,
  CreatePollInput,
  UpdatePollInput,
  VotePollInput,
  PollStats,
  PollStatus,
} from '@/services/poll.service';
import { ApiError } from '@/services/api';

// Poll state interface
interface PollState {
  polls: Poll[];
  currentPoll: Poll | null;
  stats: PollStats | null;
  filters: {
    status?: PollStatus;
    limit: number;
    offset: number;
  };
  isLoading: boolean;
  isLoadingPoll: boolean;
  isLoadingStats: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isVoting: boolean;
  error: string | null;
}

// Initial state
const initialState: PollState = {
  polls: [],
  currentPoll: null,
  stats: null,
  filters: {
    limit: 50,
    offset: 0,
  },
  isLoading: false,
  isLoadingPoll: false,
  isLoadingStats: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isVoting: false,
  error: null,
};

// Async thunks

// Fetch polls list
export const fetchPolls = createAsyncThunk<
  Poll[],
  { status?: PollStatus; limit?: number; offset?: number } | undefined,
  { rejectValue: string }
>('polls/fetchPolls', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { polls: PollState };
    const status = filters?.status ?? state.polls.filters.status;
    const limit = filters?.limit ?? state.polls.filters.limit;
    const offset = filters?.offset ?? state.polls.filters.offset;

    return await pollService.getPolls(status, limit, offset);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Umfragen');
  }
});

// Fetch single poll
export const fetchPollById = createAsyncThunk<
  Poll,
  string,
  { rejectValue: string }
>('polls/fetchPollById', async (id, { rejectWithValue }) => {
  try {
    return await pollService.getPollById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Umfrage');
  }
});

// Create poll
export const createPoll = createAsyncThunk<
  Poll,
  CreatePollInput,
  { rejectValue: string }
>('polls/createPoll', async (data, { rejectWithValue }) => {
  try {
    return await pollService.createPoll(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Umfrage');
  }
});

// Update poll
export const updatePoll = createAsyncThunk<
  Poll,
  { id: string; data: UpdatePollInput },
  { rejectValue: string }
>('polls/updatePoll', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await pollService.updatePoll(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren der Umfrage');
  }
});

// Delete poll
export const deletePoll = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('polls/deletePoll', async (id, { rejectWithValue }) => {
  try {
    await pollService.deletePoll(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen der Umfrage');
  }
});

// Vote on poll
export const votePoll = createAsyncThunk<
  Poll,
  { id: string; data: VotePollInput },
  { rejectValue: string }
>('polls/votePoll', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await pollService.votePoll(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Abstimmen');
  }
});

// Remove vote
export const removeVote = createAsyncThunk<
  Poll,
  string,
  { rejectValue: string }
>('polls/removeVote', async (id, { rejectWithValue }) => {
  try {
    return await pollService.removeVote(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Entfernen der Stimme');
  }
});

// Fetch poll stats
export const fetchPollStats = createAsyncThunk<
  PollStats,
  void,
  { rejectValue: string }
>('polls/fetchStats', async (_, { rejectWithValue }) => {
  try {
    return await pollService.getPollStats();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Statistiken');
  }
});

// Close poll
export const closePoll = createAsyncThunk<
  Poll,
  string,
  { rejectValue: string }
>('polls/closePoll', async (id, { rejectWithValue }) => {
  try {
    return await pollService.closePoll(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Schließen der Umfrage');
  }
});

// Reopen poll
export const reopenPoll = createAsyncThunk<
  Poll,
  string,
  { rejectValue: string }
>('polls/reopenPoll', async (id, { rejectWithValue }) => {
  try {
    return await pollService.reopenPoll(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Wiedereröffnen der Umfrage');
  }
});

// Slice
const pollSlice = createSlice({
  name: 'polls',
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<{ status?: PollStatus; limit?: number; offset?: number }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPoll: (state) => {
      state.currentPoll = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch polls
    builder
      .addCase(fetchPolls.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPolls.fulfilled, (state, action) => {
        state.isLoading = false;
        state.polls = action.payload;
      })
      .addCase(fetchPolls.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch poll by ID
    builder
      .addCase(fetchPollById.pending, (state) => {
        state.isLoadingPoll = true;
        state.error = null;
      })
      .addCase(fetchPollById.fulfilled, (state, action) => {
        state.isLoadingPoll = false;
        state.currentPoll = action.payload;
      })
      .addCase(fetchPollById.rejected, (state, action) => {
        state.isLoadingPoll = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Create poll
    builder
      .addCase(createPoll.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createPoll.fulfilled, (state, action) => {
        state.isCreating = false;
        state.polls.unshift(action.payload);
        state.currentPoll = action.payload;
      })
      .addCase(createPoll.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Update poll
    builder
      .addCase(updatePoll.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updatePoll.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.polls.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.polls[index] = action.payload;
        }
        if (state.currentPoll?.id === action.payload.id) {
          state.currentPoll = action.payload;
        }
      })
      .addCase(updatePoll.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Delete poll
    builder
      .addCase(deletePoll.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deletePoll.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.polls = state.polls.filter((p) => p.id !== action.payload);
        if (state.currentPoll?.id === action.payload) {
          state.currentPoll = null;
        }
      })
      .addCase(deletePoll.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Vote on poll
    builder
      .addCase(votePoll.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(votePoll.fulfilled, (state, action) => {
        state.isVoting = false;
        const index = state.polls.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.polls[index] = action.payload;
        }
        if (state.currentPoll?.id === action.payload.id) {
          state.currentPoll = action.payload;
        }
      })
      .addCase(votePoll.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Remove vote
    builder
      .addCase(removeVote.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(removeVote.fulfilled, (state, action) => {
        state.isVoting = false;
        const index = state.polls.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.polls[index] = action.payload;
        }
        if (state.currentPoll?.id === action.payload.id) {
          state.currentPoll = action.payload;
        }
      })
      .addCase(removeVote.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch stats
    builder
      .addCase(fetchPollStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchPollStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchPollStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Close poll
    builder
      .addCase(closePoll.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(closePoll.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.polls.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.polls[index] = action.payload;
        }
        if (state.currentPoll?.id === action.payload.id) {
          state.currentPoll = action.payload;
        }
      })
      .addCase(closePoll.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Reopen poll
    builder
      .addCase(reopenPoll.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(reopenPoll.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.polls.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.polls[index] = action.payload;
        }
        if (state.currentPoll?.id === action.payload.id) {
          state.currentPoll = action.payload;
        }
      })
      .addCase(reopenPoll.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });
  },
});

export const { setFilters, clearError, clearCurrentPoll } = pollSlice.actions;
export default pollSlice.reducer;
